import { getAnalyticsContext, trackClientCtaClickedSync } from '../../analytics';
import { getCrossDomainTrackingData } from '../../analytics/cross-domain-tracker';
import { isBackofficePlayground } from '../../config/deployment';
import {
    getAdditionalCtaLinkAttributionUrls,
    getCtaClickSelectors,
    getCtaParametersForwardingAllowlist,
    getCtaParametersForwardingMode,
    getSessionUtmParameterName,
    getSiteCTAs,
    getVisitorUtmParameterName,
    isCtaClickTrackingEnabled,
    isCtaLinkAttributionEnabled,
} from '../../config/SiteConfigManager';
import { roseStorage } from '../../services/rose-storage';
import { logger } from '../core/logger';
import {
    appendForwardedParams,
    type CtaParametersForwardingMode,
    getForwardedUrlParams,
} from './ctaParameterForwarding';
import { matchesUrlPattern } from './urlUtils';

interface CtaAttributionParamNames {
    sessionParamName: string | null;
    visitorParamName: string | null;
    forwardingMode: CtaParametersForwardingMode;
    forwardingAllowlist: string[];
}

interface CtaAttributionValues extends CtaAttributionParamNames {
    sessionId: string | null;
    visitorId: string | null;
    forwardedParams: URLSearchParams;
}

interface CtaAttributionTargets {
    ctaDestinations: URL[];
    additionalUrlDestinations: URL[];
    additionalUrlPatterns: string[];
}

let linkObserver: MutationObserver | null = null;
let clickHandler: ((event: Event) => void) | null = null;
let historyPatchCleanup: (() => void) | null = null;
let historyPatchRestoreTimer: number | null = null;
let activeDomain: string | null = null;

// SPA routers usually call history.pushState/replaceState synchronously after a click.
// Keep our History patch short-lived so unrelated page navigations are not affected.
const HISTORY_ATTRIBUTION_WINDOW_MS = 1000;

function normalizeHostname(hostname: string): string {
    return hostname.replace(/^www\./i, '').toLowerCase();
}

function normalizePathname(pathname: string): string {
    const normalized = pathname.replace(/\/+$/, '');
    return normalized || '/';
}

function getDestinationKey(url: URL): string {
    const port = url.port ? `:${url.port}` : '';
    const path = normalizePathname(url.pathname);
    return `${url.protocol}//${normalizeHostname(url.hostname)}${port}${path}`;
}

function buildDestinations(rawUrls: string[]): URL[] {
    const destinations = new Map<string, URL>();

    for (const rawUrl of rawUrls) {
        if (!rawUrl.trim()) continue;

        try {
            const url = new URL(rawUrl, window.location.href);
            if (!url.hostname) continue;
            destinations.set(getDestinationKey(url), url);
        } catch {
            logger.debug('CTA link attribution: skipping malformed CTA URL', {
                url: rawUrl,
            });
        }
    }

    return Array.from(destinations.values());
}

function getConfiguredCtaDestinations(domain: string): URL[] {
    const rawUrls: string[] = [];

    for (const cta of getSiteCTAs(domain)) {
        for (const langConfig of Object.values(cta.langs ?? {})) {
            if (langConfig.url?.trim()) {
                rawUrls.push(langConfig.url);
            }
        }
    }

    return buildDestinations(rawUrls);
}

function getConfiguredAdditionalUrlDestinations(rawUrls: string[]): URL[] {
    return buildDestinations(rawUrls.filter((url) => !url.includes('*')));
}

function matchesCtaDestination(target: URL, destination: URL): boolean {
    return (
        target.protocol === destination.protocol &&
        target.port === destination.port &&
        normalizeHostname(target.hostname) === normalizeHostname(destination.hostname) &&
        normalizePathname(target.pathname) === normalizePathname(destination.pathname)
    );
}

function matchesAnyCtaDestination(target: URL, destinations: URL[]): boolean {
    return destinations.some((destination) => matchesCtaDestination(target, destination));
}

function matchesAnyAdditionalUrlPattern(target: URL, patterns: string[]): boolean {
    const pathWithQuery = `${target.pathname}${target.search}${target.hash}`;
    return patterns.some(
        (pattern) =>
            matchesUrlPattern(target.toString(), pattern) ||
            matchesUrlPattern(pathWithQuery, pattern)
    );
}

function matchesAnyAttributionTarget(target: URL, targets: CtaAttributionTargets): boolean {
    return (
        matchesAnyCtaDestination(target, targets.ctaDestinations) ||
        matchesAnyCtaDestination(target, targets.additionalUrlDestinations) ||
        matchesAnyAdditionalUrlPattern(target, targets.additionalUrlPatterns)
    );
}

// A click on a link pointing to the page the visitor is already on — with no
// section anchor — is in-funnel navigation (a multi-step form "Continue"/"Back",
// or a "Log in" link on the signup page), not a top-of-funnel CTA. Skip those so
// form progression on a CTA destination page (e.g. skello.io /landing/demo) does
// not inflate CTA clicks. A non-empty #fragment is kept: that is a genuine
// same-page CTA that scrolls to a form/section (e.g. get.matera.eu "#lp-pom-block-15").
function isInFunnelSelfNavigation(anchorUrl: URL): boolean {
    return (
        normalizeHostname(anchorUrl.hostname) === normalizeHostname(window.location.hostname) &&
        normalizePathname(anchorUrl.pathname) === normalizePathname(window.location.pathname) &&
        anchorUrl.hash.replace(/^#/, '').length === 0
    );
}

function getAttributionValues(paramNames: CtaAttributionParamNames): CtaAttributionValues {
    const context = getAnalyticsContext();
    const forwardedParams = getForwardedUrlParams(
        paramNames.forwardingMode,
        paramNames.forwardingAllowlist
    );
    const crossDomainData = getCrossDomainTrackingData();

    return {
        ...paramNames,
        sessionId: context.lastActiveSessionId ?? crossDomainData.lastActiveSessionId,
        visitorId:
            context.clientId ?? roseStorage.getAnalyticsClientId() ?? crossDomainData.clientId,
        forwardedParams,
    };
}

function hasAttributionValues(attribution: CtaAttributionValues): boolean {
    if (
        (attribution.sessionParamName && attribution.sessionId) ||
        (attribution.visitorParamName && attribution.visitorId)
    ) {
        return true;
    }

    return Array.from(attribution.forwardedParams.keys()).length > 0;
}

function applyAttributionParams(url: URL, attribution: CtaAttributionValues): boolean {
    let changed = false;
    if (attribution.sessionParamName && attribution.sessionId) {
        if (url.searchParams.get(attribution.sessionParamName) !== attribution.sessionId) {
            url.searchParams.set(attribution.sessionParamName, attribution.sessionId);
            changed = true;
        }
    }
    if (attribution.visitorParamName && attribution.visitorId) {
        if (url.searchParams.get(attribution.visitorParamName) !== attribution.visitorId) {
            url.searchParams.set(attribution.visitorParamName, attribution.visitorId);
            changed = true;
        }
    }
    if (appendForwardedParams(url.searchParams, attribution.forwardedParams)) {
        changed = true;
    }
    return changed;
}

function getAnchorUrl(anchor: HTMLAnchorElement): URL | null {
    try {
        return new URL(anchor.href, window.location.href);
    } catch {
        return null;
    }
}

function decorateLink(
    anchor: HTMLAnchorElement,
    targets: CtaAttributionTargets,
    attribution: CtaAttributionValues
): void {
    const url = getAnchorUrl(anchor);
    if (!url || !matchesAnyAttributionTarget(url, targets)) return;

    if (applyAttributionParams(url, attribution)) {
        anchor.href = url.toString();
    }
}

function decorateAllLinks(
    root: ParentNode,
    targets: CtaAttributionTargets,
    attribution: CtaAttributionValues
): void {
    const anchors = root.querySelectorAll<HTMLAnchorElement>('a[href]');
    for (const anchor of anchors) {
        decorateLink(anchor, targets, attribution);
    }
}

function decorateHistoryUrl(
    rawUrl: Parameters<History['pushState']>[2],
    targets: CtaAttributionTargets,
    paramNames: CtaAttributionParamNames
): Parameters<History['pushState']>[2] {
    if (rawUrl === undefined || rawUrl === null) return rawUrl;

    const attribution = getAttributionValues(paramNames);
    if (!hasAttributionValues(attribution)) return rawUrl;

    try {
        const url = new URL(rawUrl.toString(), window.location.href);
        if (url.origin !== window.location.origin) return rawUrl;
        if (!matchesAnyAttributionTarget(url, targets)) return rawUrl;

        if (!applyAttributionParams(url, attribution)) return rawUrl;
        return url.toString();
    } catch {
        return rawUrl;
    }
}

// Some sites render links with hrefs that we can mutate, but their SPA router may still
// navigate from the original route prop, for example router.push('/contact-us').
// Patching History lets us preserve their click handlers while still attributing that route.
function setupHistoryNavigationAttribution(
    targets: CtaAttributionTargets,
    paramNames: CtaAttributionParamNames
): void {
    // In the backoffice playground the widget shares `window.history` with the backoffice's
    // react-router, so patching it here would break host navigation (see urlChange.ts).
    // CTA attribution is meaningless in the simulated preview anyway.
    if (isBackofficePlayground()) {
        return;
    }

    if (historyPatchCleanup) {
        historyPatchCleanup();
    }

    const originalPushState = window.history.pushState;
    const originalReplaceState = window.history.replaceState;

    const patchedPushState: History['pushState'] = (
        ...args: Parameters<History['pushState']>
    ): ReturnType<History['pushState']> => {
        const [data, unused, url] = args;
        return originalPushState.call(
            window.history,
            data,
            unused,
            decorateHistoryUrl(url, targets, paramNames)
        );
    };

    const patchedReplaceState: History['replaceState'] = (
        ...args: Parameters<History['replaceState']>
    ): ReturnType<History['replaceState']> => {
        const [data, unused, url] = args;
        return originalReplaceState.call(
            window.history,
            data,
            unused,
            decorateHistoryUrl(url, targets, paramNames)
        );
    };

    window.history.pushState = patchedPushState;
    window.history.replaceState = patchedReplaceState;

    historyPatchCleanup = () => {
        if (historyPatchRestoreTimer !== null) {
            window.clearTimeout(historyPatchRestoreTimer);
            historyPatchRestoreTimer = null;
        }
        if (window.history.pushState === patchedPushState) {
            window.history.pushState = originalPushState;
        }
        if (window.history.replaceState === patchedReplaceState) {
            window.history.replaceState = originalReplaceState;
        }
        historyPatchCleanup = null;
    };
}

function setupTemporaryHistoryNavigationAttribution(
    targets: CtaAttributionTargets,
    paramNames: CtaAttributionParamNames
): void {
    setupHistoryNavigationAttribution(targets, paramNames);
    historyPatchRestoreTimer = window.setTimeout(() => {
        if (historyPatchCleanup) {
            historyPatchCleanup();
        }
    }, HISTORY_ATTRIBUTION_WINDOW_MS);
}

function cleanupActiveListeners(): void {
    if (clickHandler) {
        document.removeEventListener('click', clickHandler, true);
        clickHandler = null;
    }

    if (linkObserver) {
        linkObserver.disconnect();
        linkObserver = null;
    }

    if (historyPatchCleanup) {
        historyPatchCleanup();
    }

    activeDomain = null;
}

export function setupCtaLinkAttribution(domain: string): void {
    cleanupActiveListeners();

    // Counting (emit rw_client_cta_clicked) runs whenever tracking OR attribution
    // is on; link decoration (mutating hrefs with tracking params) runs only when
    // attribution is on. isCtaClickTrackingEnabled already ORs in attribution.
    if (!isCtaClickTrackingEnabled(domain)) {
        logger.debug('CTA click tracking disabled', { domain });
        return;
    }
    const shouldDecorate = isCtaLinkAttributionEnabled(domain);

    const paramNames = {
        sessionParamName: getSessionUtmParameterName(domain),
        visitorParamName: getVisitorUtmParameterName(domain),
        forwardingMode: getCtaParametersForwardingMode(domain),
        forwardingAllowlist: getCtaParametersForwardingAllowlist(domain),
    };
    const additionalUrls = getAdditionalCtaLinkAttributionUrls(domain);
    // IX-3617: selectors for non-link CTAs (Webflow / JS div-buttons with no href).
    const clickSelectors = getCtaClickSelectors(domain);

    const targets = {
        ctaDestinations: getConfiguredCtaDestinations(domain),
        additionalUrlDestinations: getConfiguredAdditionalUrlDestinations(additionalUrls),
        additionalUrlPatterns: additionalUrls.filter((url) => url.includes('*')),
    };
    if (
        targets.ctaDestinations.length === 0 &&
        targets.additionalUrlDestinations.length === 0 &&
        targets.additionalUrlPatterns.length === 0 &&
        clickSelectors.length === 0
    ) {
        logger.debug(
            'CTA link attribution skipped: no CTA URLs, additional URLs, or selectors configured',
            { domain }
        );
        return;
    }

    activeDomain = domain;

    if (shouldDecorate) {
        const initialAttribution = getAttributionValues(paramNames);
        if (hasAttributionValues(initialAttribution)) {
            decorateAllLinks(document, targets, initialAttribution);
        }
    }

    clickHandler = (event: Event) => {
        const targetEl = event.target as Element | null;
        if (!targetEl?.closest) return;

        // Link CTAs: detected by destination URL.
        const matchedAnchor = targetEl.closest('a[href]') as HTMLAnchorElement | null;
        const anchorUrl = matchedAnchor ? getAnchorUrl(matchedAnchor) : null;
        // A configured-destination anchor only counts as a CTA when it is not
        // in-funnel self-navigation on the destination page itself (see helper).
        const anchorIsCta =
            !!anchorUrl &&
            matchesAnyAttributionTarget(anchorUrl, targets) &&
            !isInFunnelSelfNavigation(anchorUrl);

        // IX-3617: non-link CTAs (Webflow / JS div-buttons) — detected by CSS selector,
        // optionally narrowed by the button's visible text so several buttons sharing a
        // selector don't all fire (matches the exact CTA, e.g. "Essai gratuit").
        let selectorHit: { selector: string; texts: string[]; label?: string } | undefined;
        let selectorEl: Element | null = null;
        for (const s of clickSelectors) {
            let el: Element | null;
            try {
                el = targetEl.closest(s.selector);
            } catch {
                continue; // ignore a malformed selector rather than throw on the host page
            }
            if (!el) continue;
            if (s.texts.length > 0) {
                // Multilingual: match if the button's text contains ANY configured wording.
                const elText = (el.textContent ?? '').replace(/\s+/g, ' ').trim().toLowerCase();
                if (!s.texts.some((t) => elText.includes(t.toLowerCase()))) continue;
            }
            selectorHit = s;
            selectorEl = el;
            break;
        }

        if (!anchorIsCta && !selectorHit) return;

        // The click on a configured client CTA is itself the funnel signal. Emit
        // SYNCHRONOUSLY via sendBeacon: the link navigates immediately (often same-tab,
        // cross-origin), so the async event-bus path would be torn down before its
        // PostHog capture runs; a direct sendBeacon dispatched here survives navigation.
        // Non-interference: no preventDefault/stopPropagation (client handlers +
        // navigation run unchanged); the event goes only to Rose's named PostHog
        // instance, never the client's analytics; the call never throws on the host page.
        trackClientCtaClickedSync({
            destinationUrl: anchorIsCta && anchorUrl ? anchorUrl.toString() : undefined,
            ctaLabel:
                selectorHit?.label ||
                (selectorEl ?? targetEl).textContent?.trim().slice(0, 200) ||
                undefined,
            pageUrl: window.location.href,
        });

        // Param-forwarding decoration applies only when attribution is enabled
        // and only to real anchor CTAs.
        if (!shouldDecorate || !anchorIsCta || !matchedAnchor || !anchorUrl) return;
        const attribution = getAttributionValues(paramNames);
        if (!hasAttributionValues(attribution)) return;

        // First update the actual link for normal browser navigation and new-tab behavior.
        decorateLink(matchedAnchor, targets, attribution);
        if (anchorUrl.origin === window.location.origin) {
            // Then cover same-origin SPA navigation without blocking the page's onClick logic.
            setupTemporaryHistoryNavigationAttribution(targets, paramNames);
        }
    };
    document.addEventListener('click', clickHandler, true);

    if (shouldDecorate && document.body) {
        linkObserver = new MutationObserver((mutations) => {
            const attribution = getAttributionValues(paramNames);
            if (!hasAttributionValues(attribution)) return;

            for (const mutation of mutations) {
                if (mutation.type === 'childList') {
                    for (const node of mutation.addedNodes) {
                        if (node.nodeType !== Node.ELEMENT_NODE) continue;

                        const element = node as Element;
                        if (element instanceof HTMLAnchorElement) {
                            decorateLink(element, targets, attribution);
                        }
                        decorateAllLinks(element, targets, attribution);
                    }
                } else if (
                    mutation.type === 'attributes' &&
                    mutation.target instanceof HTMLAnchorElement
                ) {
                    decorateLink(mutation.target, targets, attribution);
                }
            }
        });

        linkObserver.observe(document.body, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['href'],
        });
    }

    logger.debug('CTA link attribution active', {
        domain,
        ctaUrlCount: targets.ctaDestinations.length,
        additionalUrlCount: targets.additionalUrlDestinations.length,
        additionalUrlPatternCount: targets.additionalUrlPatterns.length,
        paramNames,
    });
}

export function cleanupCtaLinkAttribution(): void {
    if (!activeDomain && !clickHandler && !linkObserver) return;

    logger.debug('CTA link attribution cleaned up', { domain: activeDomain });
    cleanupActiveListeners();
}
